import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import apiService from '../../../services/api';
import {
  CALENDAR_URL,
  EVENT_DATE_LABEL,
  EVENT_DATE_SHORT,
  EVENT_TIME_LABEL,
  VENUE_NAME,
  VENUE_LINE1,
  VENUE_LINE2
} from './graduationInviteContent';
import styles from './GraduationInviteBanner.module.css';

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

// Follow-up to the one-off graduation landing page: shown once the student has acted on it,
// standing on the dashboard until the event passes. Reuses the same message record/endpoint,
// so an RSVP submitted here updates the same row the landing page would have.
export default function GraduationInviteBanner({ message }) {
  const { user } = useAuth();
  const [rsvpResponse, setRsvpResponse] = useState(message.rsvpResponse || '');
  const [submitting, setSubmitting] = useState(false);
  const name = user?.first_name || user?.firstName || 'graduate';

  const submitRsvp = async (value) => {
    setSubmitting(true);
    try {
      await apiService.acknowledgeStudentMessage(message.id, { rsvpResponse: value });
      setRsvpResponse(value);
    } catch (error) {
      // Leave the prompt showing so the student can retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.banner}>
      {/* Wide layout: two columns split by a divider. */}
      <div className={styles.desktopRow}>
        <div className={styles.desktopLeft}>
          <div className={styles.icon} aria-hidden="true">
            <CheckIcon />
          </div>
          <div>
            <p className={styles.desktopTitle}>Congratulations, {name}!</p>
            <p className={styles.desktopSub}>
              You&rsquo;ve successfully completed the Faith Insight Foundations Program. It&rsquo;s time to celebrate your accomplishment!
            </p>
          </div>
        </div>

        <div className={styles.desktopDivider} aria-hidden="true" />

        <div className={styles.desktopRight}>
          <div className={styles.desktopRsvpSlot}>
            {rsvpResponse === 'yes' && (
              <span className={styles.confirmedBadgeSolid}>
                <a
                  className={styles.badgeCalendarLink}
                  href={CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Add to calendar"
                  title="Add to calendar"
                >
                  <CalendarIcon />
                </a>
                RSVP: Confirmed
              </span>
            )}
            {rsvpResponse === 'no' && (
              <div className={styles.rsvpStatusGroup}>
                <span className={styles.declinedBadgeSolid}>RSVP: Declined</span>
                <button type="button" className={styles.pillBtn} disabled={submitting} onClick={() => submitRsvp('yes')}>
                  Change to Yes
                </button>
              </div>
            )}
            {!rsvpResponse && (
              <div className={styles.rsvp}>
                <button type="button" className={styles.pillBtn} disabled={submitting} onClick={() => submitRsvp('yes')}>
                  Yes
                </button>
                <button type="button" className={styles.pillBtn} disabled={submitting} onClick={() => submitRsvp('no')}>
                  No
                </button>
              </div>
            )}
          </div>

          <p className={styles.desktopDateTime}>
            {EVENT_DATE_SHORT} &middot; {EVENT_TIME_LABEL}
          </p>
          <p className={styles.desktopVenue}>
            <strong>{VENUE_NAME}</strong>
            <span>{VENUE_LINE1}</span>
            <span>{VENUE_LINE2}</span>
          </p>
        </div>
      </div>

      {/* Narrow layout: stacked card with a boxed event-detail block. */}
      <div className={styles.mobileCard}>
        <div className={styles.mobileHeader}>
          <div className={styles.mobileIcon} aria-hidden="true">
            <CheckIcon />
          </div>
          <p className={styles.mobileTitle}>Congratulations, {name}!</p>
        </div>

        <p className={styles.mobileText}>
          You&rsquo;ve successfully completed the Faith Insight Foundations Program. It&rsquo;s time to celebrate your accomplishment!
        </p>

        <div className={styles.detailsBox}>
          <div className={styles.detailRow}>
            <CalendarIcon />
            <span className={styles.mobileDateTime}>{EVENT_DATE_LABEL} &middot; {EVENT_TIME_LABEL}</span>
          </div>
          <div className={styles.detailRow}>
            <PinIcon />
            <div>
              <strong>{VENUE_NAME}</strong>
              <span>{VENUE_LINE1}</span>
              <span>{VENUE_LINE2}</span>
            </div>
          </div>
        </div>

        <div className={styles.mobileActions}>
          {rsvpResponse === 'yes' && (
            <span className={styles.confirmedBadgeSolid}>
              <a
                className={styles.badgeCalendarLink}
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Add to calendar"
                title="Add to calendar"
              >
                <CalendarIcon />
              </a>
              RSVP: Confirmed
            </span>
          )}

          {rsvpResponse === 'no' && (
            <>
              <span className={styles.statusPillMobile}>Declined</span>
              <button type="button" className={styles.pillBtn} disabled={submitting} onClick={() => submitRsvp('yes')}>
                Change to Yes
              </button>
            </>
          )}

          {!rsvpResponse && (
            <>
              <button type="button" className={styles.pillBtn} disabled={submitting} onClick={() => submitRsvp('yes')}>
                Yes
              </button>
              <button type="button" className={styles.pillBtn} disabled={submitting} onClick={() => submitRsvp('no')}>
                No
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
